import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import DeleteActionButton from '../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../components/designe/ActionButtons/EditActionButton';
import AppAddPillButton from '../../../components/designe/buttons/app-add-pill-button/AppAddPillButton';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import NoResults from '../../../components/reusable/no-results';
import Search from '../../../components/reusable/search/Search';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import WithListObservers from '../../../hoc/with-list-observers';
import withRouter from '../../../hoc/with-router';
import { Paths } from '../../../shared/paths';
import { useStyles } from './ProgramSets.styles';
import ProgramSetItemSkeleton from './program-set-item-skeleton/ProgramSetItemSkeleton';
import { useProgramSetsComponent } from './useProgramSetsComponent';

const SKELETON_COUNT = 4;

const ProgramSets = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    filter,
    setFilter,
    filteredSets,
    programSetForDeleting,
    setProgramSetForDeleting,
    addNewProgramSetClicked,
    deleteProgramSet,
    isLoading,
    error,
    hasData,
    refetch,
  } = useProgramSetsComponent();
  const { navigate } = useNavigationStack();
  const showNoResult = !isLoading && !filteredSets?.length;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: error ? [error] : [],
    isLoading,
    hasData,
    onRetry: handleRetry,
  });

  return (
    <Box className={classes.programSetsListWrapper}>
      <Box display="flex" alignItems="flex-end" gap={1}>
        <Search
          fullWidth
          value={filter}
          onChange={setFilter}
          className={classes.programSetsListSearch}
        />
        {addNewProgramSetClicked && (
          <AppAddPillButton onClick={() => addNewProgramSetClicked()} />
        )}
      </Box>

      {showNoResult && <NoResults />}

      {isLoading && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.programsSetsListContainer,
            addNewProgramSetClicked &&
              classes.programsSetsListContainerWithAddButton,
          )}>
          <Box display="flex" flexDirection="column">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
              <ProgramSetItemSkeleton key={i} />
            ))}
          </Box>
        </WithListObservers>
      )}

      {!isLoading && filteredSets && filteredSets.length > 0 && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.programsSetsListContainer,
            addNewProgramSetClicked &&
              classes.programsSetsListContainerWithAddButton,
          )}>
          {filteredSets.map((set) => (
            <Box
              className={classes.programItemSetsWrapper}
              key={set.id}
              onClick={() => navigate(Paths.programs.toViewSetId(set.id))}>
              <Box className={classes.programSetsItemInfoWrapper}>
                <Typography variant="h3">{set.title}</Typography>
                <Typography
                  variant={'success-wrapped-sm'}
                  className={classes.programSetsItemExercise}>
                  {set?.exercise?.title}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                {set?.permissions?.canEdit && (
                  <EditActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(Paths.programs.toEditSetId(set.id));
                    }}
                  />
                )}
                {set?.permissions?.canDelete && (
                  <DeleteActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setProgramSetForDeleting(set);
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </WithListObservers>
      )}

      <ConfirmDialog
        open={!!programSetForDeleting}
        onClose={() => setProgramSetForDeleting(null)}
        title={t('programs.deleteProgramSet')}
        onSubmit={() =>
          deleteProgramSet({ setId: programSetForDeleting.id })
            .unwrap()
            .then((response) => setProgramSetForDeleting(null))
            .catch((e) => console.log(e))
        }>
        {t('programs.deleteProgramSetQuestion')}
      </ConfirmDialog>
    </Box>
  );
};

export default withRouter(ProgramSets);
